import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { COMPANY, PLATFORMS, LEADERSHIP, MEDIA_RECOGNITION, TECHNOLOGY_FOCUS } from '../data/content';
import SEO from '../components/SEO';

const Home = () => {
  return (
    <div className="bg-[#050505] text-white" data-testid="home-page">
      <SEO
        title="Bronto Platforms — Building the Platforms Behind the Bronto Ecosystem"
        description="Bronto Platforms Private Limited is the product and platform subsidiary of Bronto Intelligence Private Limited, building and operating BrontoAI, BrontoY, MSPConnect, and Bronto Cosmos."
        path="/"
      />
      {/* ===== HERO ===== */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden">
        <div className="aurora-bg" />
        <div className="absolute inset-0 grid-pattern opacity-30" />
        <div className="noise-overlay" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 w-full py-24">
          <div className="max-w-5xl">
            <h1
              className="text-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl mb-8 fade-in-up delay-100"
              data-testid="hero-headline"
            >
              Building the Platforms
              <br />
              Behind the
              <br />
              <span className="rainbow-text">Bronto Ecosystem.</span>
            </h1>

            <p className="text-lg md:text-xl text-white/65 max-w-3xl leading-relaxed mb-12 fade-in-up delay-200" data-testid="hero-subheadline">
              {COMPANY.legalName} is the product and platform subsidiary of {COMPANY.parentName}, building and operating BrontoAI, BrontoY, MSPConnect, and Bronto Cosmos.
            </p>

            <div className="flex flex-wrap items-center gap-4 fade-in-up delay-300">
              <Link to="/about" className="btn-primary" data-testid="hero-cta-explore">
                Explore Bronto Platforms
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/platforms" className="btn-secondary" data-testid="hero-cta-platforms">
                View Platforms
              </Link>
              <Link to="/leadership" className="btn-secondary" data-testid="hero-cta-leadership">
                Meet Leadership
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 fade-in delay-600">
          <span className="text-[10px] uppercase tracking-[0.3em] text-white/40">Scroll</span>
          <div className="w-px h-12 bg-gradient-to-b from-white/40 to-transparent" />
        </div>
      </section>

      {/* ===== COMPANY STATEMENT ===== */}
      <section className="relative py-32 px-6 lg:px-10">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-12 gap-12">
            <div className="md:col-span-4">
              <div className="text-xs font-semibold uppercase tracking-[0.25em] text-white/40 mb-4">
                Who We Are
              </div>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
                A subsidiary of Bronto Intelligence.
              </h2>
            </div>
            <div className="md:col-span-8">
              <p className="text-xl md:text-2xl text-white/85 leading-relaxed font-light">
                {COMPANY.description}
              </p>
              <p className="mt-6 text-base md:text-lg text-white/55 leading-relaxed">
                {COMPANY.longDescription}
              </p>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 mt-10 text-sm font-medium text-white border-b border-white/30 pb-1 hover:border-white transition-colors"
                data-testid="about-learn-more"
              >
                Learn more about us
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ===== PLATFORMS SHOWCASE ===== */}
      <section className="relative py-32 px-6 lg:px-10 bg-gradient-to-b from-transparent via-white/[0.02] to-transparent">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.25em] text-white/40 mb-4">
                Platforms
              </div>
              <h2 className="text-4xl md:text-6xl font-bold tracking-tight max-w-3xl">
                The flagship platforms <br />
                of the Bronto ecosystem.
              </h2>
            </div>
            <Link to="/platforms" className="btn-secondary self-start" data-testid="view-all-platforms">
              View All Platforms
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {PLATFORMS.map((platform) => (
              <Link
                key={platform.id}
                to={`/${platform.id}`}
                className="group relative overflow-hidden rounded-3xl glass hover-lift p-10 md:p-12 min-h-[480px] flex flex-col justify-between"
                data-testid={`platform-card-${platform.id}`}
              >
                {/* Background gradient orb */}
                <div
                  className={`absolute -top-32 -right-32 w-96 h-96 rounded-full opacity-20 blur-3xl bg-gradient-to-br ${platform.gradient} transition-all duration-700 group-hover:opacity-40 group-hover:scale-110`}
                />

                <div className="relative z-10">
                  <div className="text-xs font-semibold uppercase tracking-[0.25em] text-white/40 mb-4">
                    {platform.category}
                  </div>
                  <h3 className={`text-5xl md:text-6xl font-bold tracking-tight mb-6 bg-gradient-to-r ${platform.gradient} bg-clip-text text-transparent`}>
                    {platform.name}
                  </h3>
                  <p className="text-base md:text-lg text-white/70 leading-relaxed max-w-md">
                    {platform.shortDescription}
                  </p>
                </div>

                <div className="relative z-10 flex items-center justify-between mt-12">
                  <span className="text-sm font-medium text-white/80 group-hover:text-white transition-colors">
                    Explore {platform.name}
                  </span>
                  <div className="w-12 h-12 rounded-full bg-white/10 group-hover:bg-white group-hover:text-black flex items-center justify-center transition-all duration-500">
                    <ArrowUpRight className="h-5 w-5" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ===== LEADERSHIP PREVIEW ===== */}
      <section className="relative py-32 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <div className="text-xs font-semibold uppercase tracking-[0.25em] text-white/40 mb-4">
              Leadership
            </div>
            <h2 className="text-4xl md:text-6xl font-bold tracking-tight max-w-3xl mx-auto">
              Guided by experienced
              <br />
              strategic leadership.
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {LEADERSHIP.map((leader) => (
              <Link
                key={leader.id}
                to={`/leadership/${leader.id}`}
                className="group relative overflow-hidden rounded-3xl glass hover-lift p-8 md:p-10"
                data-testid={`leader-card-${leader.id}`}
              >
                <div className="flex flex-col sm:flex-row gap-6 items-start">
                  {/* Photo */}
                  <div className="relative shrink-0">
                    <div className="w-28 h-28 md:w-32 md:h-32 rounded-2xl overflow-hidden bg-white/5 ring-1 ring-white/10">
                      {leader.photoUrl ? (
                        <img
                          src={leader.photoUrl}
                          alt={leader.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-2xl font-bold text-white/30">
                          {leader.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-2xl font-bold tracking-tight mb-1.5">{leader.name}</h3>
                    <p className="text-sm font-medium text-white/55 mb-4">{leader.title}</p>
                    <p className="text-sm text-white/65 leading-relaxed line-clamp-3">
                      {leader.shortBio}
                    </p>
                    <div className="mt-5 inline-flex items-center gap-1.5 text-xs font-medium text-white/80 group-hover:text-white transition-colors">
                      View full profile
                      <ArrowUpRight className="h-3.5 w-3.5" />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ===== TECHNOLOGY FOCUS ===== */}
      <section className="relative py-32 px-6 lg:px-10 bg-gradient-to-b from-transparent via-white/[0.02] to-transparent">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16">
            <div className="text-xs font-semibold uppercase tracking-[0.25em] text-white/40 mb-4">
              Technology Focus
            </div>
            <h2 className="text-4xl md:text-6xl font-bold tracking-tight max-w-4xl">
              Working across the foundations
              <br />
              of intelligent systems.
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 rounded-3xl overflow-hidden">
            {TECHNOLOGY_FOCUS.map((item, idx) => (
              <div
                key={item.title}
                className="bg-[#050505] p-8 md:p-10 hover:bg-white/[0.03] transition-colors group"
                data-testid={`tech-focus-${idx}`}
              >
                <div className="text-xs font-semibold text-white/30 mb-3">0{idx + 1}</div>
                <h3 className="text-2xl font-bold tracking-tight mb-3">{item.title}</h3>
                <p className="text-sm text-white/55 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== MEDIA RECOGNITION ===== */}
      <section className="relative py-32 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.25em] text-white/40 mb-4">
                Media & Recognition
              </div>
              <h2 className="text-4xl md:text-6xl font-bold tracking-tight max-w-3xl">
                In the press.
              </h2>
            </div>
            <Link to="/media" className="btn-secondary self-start" data-testid="view-all-media">
              View All Coverage
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {MEDIA_RECOGNITION.map((item) => (
              <a
                key={item.id}
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group glass rounded-2xl p-6 hover-lift block"
                data-testid={`media-card-${item.id}`}
              >
                <div className="flex items-center justify-between mb-6">
                  <span className="text-xs font-semibold text-white/60 uppercase tracking-wider">
                    {item.publication}
                  </span>
                  <ArrowUpRight className="h-4 w-4 text-white/40 group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-base font-semibold leading-snug mb-4 line-clamp-3">
                  {item.headline}
                </h3>
                <div className="flex items-center gap-2 text-[11px] text-white/35">
                  <span>{item.language}</span>
                  <span>·</span>
                  <span>{item.date}</span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA SECTION ===== */}
      <section className="relative py-32 px-6 lg:px-10">
        <div className="max-w-5xl mx-auto">
          <div className="relative rounded-3xl glass-strong p-12 md:p-20 text-center overflow-hidden">
            <div className="absolute inset-0 grid-pattern opacity-50" />
            <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-96 h-96 rounded-full blur-3xl opacity-20 bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500" />

            <div className="relative z-10">
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
                Build the future
                <br />
                <span className="rainbow-text">with us.</span>
              </h2>
              <p className="text-base md:text-lg text-white/60 max-w-2xl mx-auto mb-10">
                Connect with Bronto Platforms for partnerships, media inquiries, or career opportunities.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4">
                <Link to="/contact" className="btn-primary" data-testid="cta-contact">
                  Contact Us
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/careers" className="btn-secondary" data-testid="cta-careers">
                  Careers
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
